'use client';

import { FC } from 'react';
import { Content } from '@prismicio/client';
import { PrismicNextImage, PrismicNextLink } from '@prismicio/next';
import { PrismicRichText, SliceComponentProps } from '@prismicio/react';

/**
 * Props for `TestimonialHighlight2`.
 */
export type TestimonialHighlight2Props =
  SliceComponentProps<Content.TestimonialHighlight2Slice>;

/**
 * Component for "TestimonialHighlight2" Slices.
 */
const TestimonialHighlight2: FC<TestimonialHighlight2Props> = ({ slice }) => {
  const {
    company_logo,
    headline,
    testimonial_body,
    rating,
    client_name,
    client_position,
    case_study_link,
  } = slice.primary;

  // Generate star rating display
  const renderStars = (ratingValue: number | null | undefined) => {
    if (!ratingValue) return null;

    const stars = Math.min(Math.max(Math.round(ratingValue), 0), 5);
    return (
      <div
        className="flex gap-[1px] items-center h-4 w-[150px]"
        role="img"
        aria-label={`${stars} out of 5 stars`}
      >
        {Array.from({ length: 5 }).map((_, index) => (
          <svg
            key={index}
            className={`w-[16.67px] h-4 ${index < stars ? 'text-[#FFA500]' : 'text-gray-400'}`}
            fill="currentColor"
            viewBox="0 0 20 20"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </svg>
        ))}
      </div>
    );
  };

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="bg-[#0f423a] box-border flex flex-col gap-[50px] items-start px-4 md:px-8 lg:px-[9rem] py-[80px] relative w-full"
    >
      <div className="flex gap-6 items-center relative w-full">
        <PrismicNextLink
          field={case_study_link}
          className="w-full bg-[rgba(255,255,255,0.1)] box-border flex flex-col gap-6 items-start p-6 relative rounded-2xl shadow-[0px_4.325px_64.871px_0px_rgba(0,0,0,0.08)] transition-all duration-300 ease-in-out hover:scale-[1.02] hover:shadow-[0px_8px_80px_0px_rgba(5,242,115,0.15)] hover:bg-[rgba(255,255,255,0.15)] cursor-pointer group"
        >
          {/* Header Section with Logo and Arrow */}
          <div className="flex flex-col gap-4 items-start relative w-full">
            <div className="flex items-center justify-between relative w-full">
              {/* Company Logo */}
              {company_logo && company_logo.url && (
                <div className="h-[19px] relative w-[150px]">
                  <PrismicNextImage
                    field={company_logo}
                    className="block max-w-none h-full w-full object-contain"
                  />
                </div>
              )}

              {/* Arrow Icon */}
              <div className="flex h-0 items-center justify-center relative w-0">
                <div className="flex-none transition-transform duration-300 ease-in-out group-hover:translate-x-1 group-hover:-translate-y-1">
                  <div
                    className="relative"
                    style={{ width: '39.242px', height: '39.242px' }}
                  >
                    <svg
                      className="block max-w-none w-full h-full"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M7 17L17 7M17 7H7M17 7V17"
                        stroke="#05F273"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                </div>
              </div>
            </div>

            {/* Headline and Stars Row */}
            <div className="flex gap-2.5 items-center justify-between relative w-full">
              {/* Headline */}
              {headline && (
                <div className="flex-1 min-w-0">
                  <div
                    className="text-white"
                    style={{
                      fontFamily: 'Inter',
                      fontSize: '18px',
                      fontStyle: 'normal',
                      fontWeight: 600,
                      lineHeight: '150%',
                    }}
                  >
                    <PrismicRichText field={headline} />
                  </div>
                </div>
              )}

              {/* Star Rating */}
              {rating && (
                <div className="flex-shrink-0">{renderStars(rating)}</div>
              )}
            </div>
          </div>

          {/* Body Section */}
          <div className="flex flex-col gap-4 items-start relative w-full">
            {/* Testimonial Body */}
            {testimonial_body && (
              <div
                className="w-full prose prose-invert max-w-none"
                style={{
                  color: '#D7EDE3',
                  fontFamily: 'Inter',
                  fontSize: '16px',
                  fontStyle: 'normal',
                  fontWeight: 400,
                  lineHeight: '150%',
                }}
              >
                <PrismicRichText field={testimonial_body} />
              </div>
            )}

            {/* Client Info */}
            {(client_name || client_position) && (
              <div
                className="flex flex-col gap-1 items-start whitespace-pre"
                style={{
                  color: '#FFF',
                  fontFamily: 'Inter',
                  fontSize: '16px',
                  fontStyle: 'normal',
                  fontWeight: 400,
                  lineHeight: '150%',
                }}
              >
                {client_name && <p className="relative">{client_name}</p>}
                {client_position && (
                  <p className="relative">{client_position}</p>
                )}
              </div>
            )}
          </div>
        </PrismicNextLink>
      </div>
    </section>
  );
};

export default TestimonialHighlight2;
